/**
 * <parts-finder> — a Year -> Make -> Model vehicle parts finder.
 *
 * Three dependent <select>s that, on submit, send the shopper to the matching chassis
 * collection page. No backend: the whole cascade runs client-side off a single JSON file
 * (assets/parts_finder_data.json) fetched at runtime from the URL in `data-src`.
 *
 * Cascade
 *   Year   -> only years that actually have products (any chassis with count > 0 whose
 *             [year_start, year_end] range covers that year), newest first.
 *   Make   -> sorted unique makes with a chassis available in the chosen year.
 *   Model  -> model_display of chassis matching the chosen year AND make, alphabetical;
 *             each option's value is the chassis collection_url.
 * Re-selecting an earlier dropdown resets the ones after it.
 *
 * Self-contained and scoped to `this`, so several instances on one page never collide.
 * The fetched data is shared across instances via a static cache (one network request).
 *
 * Graceful degradation: if the JSON fails to load or is empty, the form is hidden and the
 * always-present fallback hint (search + contact links) carries the shopper instead. Nothing
 * throws.
 */

import { addVehicle } from '@theme/garage-store';

class PartsFinder extends HTMLElement {
  /** @type {Map<string, Promise<any>>} url -> parsed-JSON promise, shared by all instances */
  static #dataCache = new Map();

  /** @type {HTMLSelectElement | null} */ #year = null;
  /** @type {HTMLSelectElement | null} */ #make = null;
  /** @type {HTMLSelectElement | null} */ #model = null;
  /** @type {HTMLButtonElement | null} */ #submit = null;
  /** @type {HTMLFormElement | null} */ #form = null;
  /** @type {any} */ #data = null;
  #autoRedirect = false;
  #saveVehicle = true;
  #src = null;
  #loaded = false;

  connectedCallback() {
    this.#form = this.querySelector('[data-pf-form]');
    this.#year = this.querySelector('[data-pf-year]');
    this.#make = this.querySelector('[data-pf-make]');
    this.#model = this.querySelector('[data-pf-model]');
    this.#submit = this.querySelector('[data-pf-submit]');
    this.#autoRedirect = this.getAttribute('data-auto-redirect') === 'true';
    this.#saveVehicle = this.getAttribute('data-save-vehicle') !== 'false';

    const src = this.getAttribute('data-src');
    if (!this.#form || !this.#year || !this.#make || !this.#model || !src) return;

    this.#year.addEventListener('change', this.#onYearChange);
    this.#make.addEventListener('change', this.#onMakeChange);
    this.#model.addEventListener('change', this.#onModelChange);
    this.#form.addEventListener('submit', this.#onSubmit);

    // Eager by default (visible body sections). With `data-defer`, hold the fetch until
    // ensureLoaded() is called — the header garage uses this so the 45KB data file only loads
    // when the shopper first opens the modal, not on every page.
    this.#src = src;
    if (!this.hasAttribute('data-defer')) this.ensureLoaded();
  }

  /** Kick off the one-time data load (idempotent). Called on connect, or lazily by the garage. */
  ensureLoaded() {
    if (this.#loaded || !this.#src) return;
    this.#loaded = true;
    this.#load(this.#src);
  }

  async #load(src) {
    try {
      const data = await PartsFinder.#fetchData(src);
      if (!data || !Array.isArray(data.vehicles) || data.vehicles.length === 0) {
        throw new Error('parts-finder: no vehicle data');
      }
      this.#data = data;
      this.#populateYears();
      this.removeAttribute('aria-busy');
      this.dataset.state = 'ready';
    } catch (error) {
      this.#fail(error);
    }
  }

  /** One fetch per URL, reused across every instance on the page. */
  static #fetchData(url) {
    if (!PartsFinder.#dataCache.has(url)) {
      const promise = fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`parts-finder: HTTP ${response.status}`);
        return response.json();
      });
      // Don't cache a rejected promise — let a later instance retry.
      promise.catch(() => PartsFinder.#dataCache.delete(url));
      PartsFinder.#dataCache.set(url, promise);
    }
    return PartsFinder.#dataCache.get(url);
  }

  /**
   * Years that actually have products: every year covered by at least one chassis with
   * count > 0, clamped to the dataset's meta range, newest first.
   */
  #populateYears() {
    const meta = this.#data.meta || {};
    const floor = Number.isFinite(Number(meta.year_min)) ? Number(meta.year_min) : -Infinity;
    const ceil = Number.isFinite(Number(meta.year_max)) ? Number(meta.year_max) : Infinity;

    const years = new Set();
    for (const vehicle of this.#data.vehicles) {
      if (!(Number(vehicle.count) > 0)) continue;
      const start = Math.max(Number(vehicle.year_start), floor);
      const end = Math.min(Number(vehicle.year_end), ceil);
      for (let y = start; y <= end; y += 1) years.add(y);
    }

    const sorted = [...years].sort((a, b) => b - a);
    this.#fill(this.#year, sorted.map((y) => ({ value: String(y), label: String(y) })), this.#year.dataset.placeholder);
    this.#year.disabled = sorted.length === 0;
  }

  #onYearChange = () => {
    this.#reset(this.#make);
    this.#reset(this.#model);
    this.#updateSubmit();

    const year = Number(this.#year.value);
    if (!year) return;

    const makes = [
      ...new Set(
        this.#data.vehicles
          .filter((v) => Number(v.count) > 0 && v.year_start <= year && year <= v.year_end)
          .map((v) => v.make)
      ),
    ].sort((a, b) => a.localeCompare(b));

    this.#fill(this.#make, makes.map((m) => ({ value: m, label: m })), this.#make.dataset.placeholder);
    this.#make.disabled = makes.length === 0;
  };

  #onMakeChange = () => {
    this.#reset(this.#model);
    this.#updateSubmit();

    const year = Number(this.#year.value);
    const make = this.#make.value;
    if (!year || !make) return;

    const models = this.#data.vehicles
      .filter((v) => Number(v.count) > 0 && v.make === make && v.year_start <= year && year <= v.year_end)
      .sort((a, b) => a.model_display.localeCompare(b.model_display))
      .map((v) => ({ value: v.collection_url, label: v.model_display }));

    this.#fill(this.#model, models, this.#model.dataset.placeholder);
    this.#model.disabled = models.length === 0;
  };

  #onModelChange = () => {
    this.#updateSubmit();
    if (this.#autoRedirect && this.#model.value) this.#go(this.#model.value);
  };

  #onSubmit = (event) => {
    event.preventDefault();
    if (this.#model.value) this.#go(this.#model.value);
  };

  #go(url) {
    this.#saveGarage();
    window.location.href = url;
  }

  /**
   * Add the chosen car to My Garage and make it active, so the header button + any Algolia
   * fitment hook reflect it. Delegates to the shared garage-store (which owns the localStorage
   * list/active contract + `vehicle:change` event). The garage's modal hosts this same component,
   * so picking a car there adds it; picking one in a standalone section adds it too.
   */
  #saveGarage() {
    if (!this.#saveVehicle) return;
    const url = this.#model.value;
    if (!url) return;
    const model = this.#model.selectedOptions[0]?.textContent?.trim() || '';
    const label = [this.#year.value, this.#make.value, model].filter(Boolean).join(' ');
    addVehicle({ label, url });
  }

  /** Replace a select's options with a placeholder followed by `options`. */
  #fill(select, options, placeholder) {
    select.replaceChildren();
    if (placeholder != null) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = placeholder;
      select.append(opt);
    }
    for (const { value, label } of options) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      select.append(opt);
    }
    select.value = '';
  }

  /** Empty a downstream select back to just its placeholder and disable it. */
  #reset(select) {
    this.#fill(select, [], select.dataset.placeholder);
    select.disabled = true;
  }

  #updateSubmit() {
    if (this.#submit) this.#submit.disabled = !this.#model.value;
  }

  #fail(error) {
    if (error) console.warn(error);
    this.dataset.state = 'unavailable';
    this.removeAttribute('aria-busy');
    if (this.#form) this.#form.hidden = true;
  }
}

if (!customElements.get('parts-finder')) {
  customElements.define('parts-finder', PartsFinder);
}
